import {
  ClockCircleOutline,
  ExclamationCircleOutline,
  StarFill,
} from 'antd-mobile-icons';
import DashboardPackCard, {
  type PackCardAction,
  type PackCardMetric,
  type PackCardNotice,
  type PackCardQueueItem,
  type PackCardTone,
} from '../molecules/DashboardPackCard';
import { EnergyBatteryIcon } from '../icons';
import type { ActivePack } from '../../services/api/types';
import { formatAh } from '../../services/api/numberUtils';
import { formatRemainingHours, formatValidityHours } from '../../utils/packValidity';

interface ActivePackCardProps {
  activePack: ActivePack | null;
  queuedPacks: ActivePack[];
  onBuyPack?: () => void;
  onViewPacks?: () => void;
}

const statusTone = (statusColor: ActivePack['status_color']): PackCardTone => statusColor;

const formatStatus = (status: ActivePack['status']): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'queued':
    case 'reserved':
      return 'Queued';
    case 'expired':
      return 'Expired';
    case 'consumed':
      return 'Consumed';
    case 'depleted':
      return 'Depleted';
    default:
      return status;
  }
};

const formatPackType = (packType: ActivePack['pack_type']) => {
  switch (packType) {
    case 'hybrid':
      return 'Hybrid';
    case 'energy_only':
      return 'Energy only';
    case 'time_based':
      return 'Time based';
    default:
      return packType;
  }
};

const percentRemaining = (remaining: number, total?: number) => {
  if (!total || total <= 0) return null;
  return (remaining / total) * 100;
};

const getUrgencyLevel = (pack: ActivePack): 'normal' | 'warning' | 'critical' => {
  const ahPercent = pack.ah_limit ? (pack.ah_remaining / pack.ah_limit) * 100 : 100;
  const timePercent = pack.validity_hours ? (pack.hours_remaining / pack.validity_hours) * 100 : 100;
  const minimumPercent = Math.min(ahPercent, timePercent);

  if (minimumPercent <= 10) return 'critical';
  if (minimumPercent <= 30) return 'warning';
  return 'normal';
};

const formatQueueDetail = (pack: ActivePack, activePack: ActivePack | null, index: number) => {
  const details: string[] = [];
  const validityLabel = formatValidityHours(pack.validity_hours);

  if (pack.ah_limit) details.push(`${pack.ah_limit.toFixed(1)} Ah`);
  if (validityLabel) details.push(validityLabel);
  if (pack.designated_next) {
    details.push('Next');
  } else if (index === 0 && activePack?.hours_remaining && activePack.hours_remaining < 24) {
    details.push(`Activates in about ${Math.round(activePack.hours_remaining)}h`);
  }

  return details.join(' / ');
};

const buildQueueItems = (queuedPacks: ActivePack[], activePack: ActivePack | null): PackCardQueueItem[] =>
  queuedPacks.slice(0, 3).map((pack, index) => ({
    key: pack.pack_id,
    title: pack.template_name,
    detail: formatQueueDetail(pack, activePack, index),
    badge: pack.designated_next
      ? (
        <>
          <StarFill style={{ marginRight: 4 }} />
          NEXT
        </>
      )
      : pack.priority_in_queue
        ? `#${pack.priority_in_queue}`
        : undefined,
  }));

const ActivePackCard = ({
  activePack,
  queuedPacks,
  onBuyPack,
  onViewPacks,
}: ActivePackCardProps) => {
  const queueItems = buildQueueItems(queuedPacks, activePack);
  const queueOverflowCount = Math.max(queuedPacks.length - queueItems.length, 0);

  if (!activePack) {
    return (
      <DashboardPackCard
        title="Energy Pack"
        icon={<EnergyBatteryIcon style={{ width: 16, height: 16 }} />}
        statusLabel="No Pack"
        statusTone="grey"
        emptyState={{
          title: 'No active energy pack',
          description: queuedPacks.length > 0
            ? `You have ${queuedPacks.length} energy pack${queuedPacks.length === 1 ? '' : 's'} waiting to activate.`
            : 'Purchase an energy pack to start riding.',
          action: onBuyPack
            ? {
              label: 'Buy Energy Pack',
              onClick: onBuyPack,
            }
            : undefined,
        }}
        queueTitle={queuedPacks.length > 0 ? 'Queued energy packs' : undefined}
        queueItems={queueItems}
        queueOverflowCount={queueOverflowCount}
      />
    );
  }

  const tone = statusTone(activePack.status_color);
  const urgency = getUrgencyLevel(activePack);
  const totalValidityLabel = formatValidityHours(activePack.validity_hours);

  const metrics: PackCardMetric[] = [
    ...(activePack.ah_limit
      ? [{
        key: 'energy',
        icon: <EnergyBatteryIcon style={{ width: 18, height: 18 }} />,
        label: 'Energy Remaining',
        value: formatAh(activePack.ah_remaining),
        detail: `of ${activePack.ah_limit.toFixed(1)} Ah`,
        percent: percentRemaining(activePack.ah_remaining, activePack.ah_limit),
        tone,
      }]
      : []),
    ...(activePack.validity_hours
      ? [{
        key: 'time',
        icon: <ClockCircleOutline />,
        label: 'Time Left',
        value: formatRemainingHours(activePack.hours_remaining),
        detail: `of ${totalValidityLabel} total`,
        percent: percentRemaining(activePack.hours_remaining, activePack.validity_hours),
        tone,
      }]
      : []),
  ];

  const notice: PackCardNotice | undefined = urgency === 'normal'
    ? undefined
    : {
      tone: urgency === 'critical' ? 'critical' : 'warning',
      icon: <ExclamationCircleOutline />,
      content: urgency === 'critical'
        ? 'Energy pack is almost depleted. Buy a new pack soon.'
        : 'Energy pack is running low. Consider purchasing a new pack.',
    };

  const actions: PackCardAction[] = [];

  if (onBuyPack) {
    actions.push({
      key: 'buy',
      label: 'Buy More',
      onClick: onBuyPack,
    });
  }

  if (onViewPacks) {
    actions.push({
      key: 'view',
      label: 'View Energy Packs',
      fill: 'outline',
      onClick: onViewPacks,
    });
  }

  return (
    <DashboardPackCard
      title="Energy Pack"
      icon={<EnergyBatteryIcon style={{ width: 16, height: 16 }} />}
      statusLabel={formatStatus(activePack.status)}
      statusTone={tone}
      packName={activePack.template_name}
      packMeta={formatPackType(activePack.pack_type)}
      metrics={metrics}
      notice={notice}
      queueTitle={queuedPacks.length > 0 ? `Next: ${queuedPacks.length} energy pack${queuedPacks.length === 1 ? '' : 's'} queued` : undefined}
      queueItems={queueItems}
      queueOverflowCount={queueOverflowCount}
      actions={actions}
    />
  );
};

export default ActivePackCard;
